import {
  locationTemplate,
  locationController,
  travelExpenseTypeTemplate,
  travelExpenseTypeController
} from "../modals"


import moment from 'moment';
import { uniq } from 'lodash'

class TravelExpenseDetailsController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService) {
    "ngInject";

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.activeUsers = [];
    this.$uibModal = $uibModal;
    this.travelExpense = {
      expenses: []
    };
    this.projects = [];
    this.statusOptions = {
      buttonText: 'Submit for Approval',
      nextStatus: 'SUBMITTED',
    }

    this.travelExpenseStatusDetails = {}
    this.travelExpenseService = TravelExpenseService
    this.baselineFingerprint = null;
    this.hasSavedChanges = false;

    this.$http.get($rootScope.apiHost+"/api/users/me", {withCredentials: true}).then((response) => {
      this.projects = uniq(response.data.availableChargeCodes
        .filter((cc) => cc.billable && cc.taskId && cc.taskName)
        .map((cc) => ({ 
          taskId: cc.taskId,
          taskName: cc.taskName,
        })))
      const wasClean = !this.isDirty();
      this.reconcileBillablePurpose();
      // Re-baseline after async project load so automatic purpose reconciliation
      // doesn't count as a user edit that unlocks resubmit.
      if (wasClean && this.baselineFingerprint && !this.hasSavedChanges) {
        this.captureBaseline();
      }
    });

    this.loading = true
    this.$http.get($rootScope.apiHost+"/api/expense-reports/"+$stateParams.expenseReportId, {withCredentials: true}).then((success) => {
      this.loading = false
      const report = this.travelExpenseService.normalizeExpenseReportDates(success.data);
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.travelExpenseService.hydrateMileageDisplay(report);
      this.travelExpense = report;
      this.travelExpense.expenses = report.expenses || [];
      if (this.travelExpense.project != null && this.travelExpense.project !== '') {
        this.travelExpense.project = Number(this.travelExpense.project);
      } else {
        this.travelExpense.project = null;
      }
      this.travelExpenseStatusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(this.travelExpense.status)
      this.reconcileBillablePurpose();
      this.captureBaseline();
    });

    // Update status options according to the current status
    $scope.$watch(() => this.travelExpenseStatusDetails, (newVal) => {
      if (!newVal) return

      if (newVal.value === 'APPROVED') {
        this.statusOptions.buttonText = 'Submit for Reimbursement'
        this.statusOptions.nextStatus = 'REIMBURSEMENT_REQUESTED'
      } else if (newVal.value === 'REIMBURSEMENT_REJECTED') {
        this.statusOptions.buttonText = 'Resubmit for Reimbursement'
        this.statusOptions.nextStatus = 'REIMBURSEMENT_REQUESTED'
      } else if (newVal.value === 'REJECTED') {
        this.statusOptions.buttonText = 'Resubmit for Approval'
        this.statusOptions.nextStatus = 'SUBMITTED'
      } else {
        this.statusOptions.buttonText = 'Submit for Approval'
        this.statusOptions.nextStatus = 'SUBMITTED'
      }
    })
  }

  hasBillableProjects() {
    return this.projects && this.projects.length > 0;
  }

  isBillableTrip() {
    return this.travelExpense.purpose === 'customer';
  }

  isRejected() {
    const status = this.travelExpense && this.travelExpense.status;
    return status === 'REJECTED' || status === 'REIMBURSEMENT_REJECTED';
  }

  isReimbursementRejected() {
    return this.travelExpense && this.travelExpense.status === 'REIMBURSEMENT_REJECTED';
  }

  captureBaseline() {
    this.baselineFingerprint = this.getReportFingerprint(this.travelExpense);
    this.hasSavedChanges = false;
  }

  getReportFingerprint(report) {
    if (!report) {
      return '';
    }

    const expenses = (report.expenses || []).map((expense) => ({
      id: expense.id || null,
      itinerary: !!expense.itinerary,
      type: expense.type || '',
      description: (expense.description || '').trim(),
      amount: Number(expense.amount || 0),
      mileage: expense.mileage == null ? null : Number(expense.mileage),
      place: expense.place || '',
      zip: expense.zip || '',
      fromDate: expense.fromDate ? this.moment(expense.fromDate).format('YYYY-MM-DD') : null,
      toDate: expense.toDate ? this.moment(expense.toDate).format('YYYY-MM-DD') : null,
      daysTraveled: Number(expense.daysTraveled || 0),
      mealsRate: Number(expense.mealsRate || 0),
      lodgingRate: Number(expense.lodgingRate || 0),
      hasPendingFile: !!expense.estimateBasisFile,
      pendingReceiptDeletes: (expense.pendingReceiptIdsToDelete || []).slice().sort()
    }));

    return JSON.stringify({
      name: (report.name || '').trim(),
      purpose: report.purpose || '',
      project: report.project == null || report.project === '' ? '' : String(report.project),
      description: (report.description || '').trim(),
      expenses
    });
  }

  isDirty() {
    if (!this.baselineFingerprint) {
      return false;
    }

    return this.getReportFingerprint(this.travelExpense) !== this.baselineFingerprint;
  }

  canResubmit() {
    if (!this.isRejected()) {
      return this.hasPositiveEstimate();
    }

    if (!this.isDirty() && !this.hasSavedChanges) {
      return false;
    }

    return this.hasPositiveEstimate();
  }

  canSave() {
    if (this.isLocked()) {
      return false;
    }

    return this.isDirty();
  }

  hasPositiveEstimate() {
    return this.travelExpenseService.getEstimatedTotalForExpenseReport(this.travelExpense) > 0;
  }

  getAdditionalExpensesTotal() {
    return (this.travelExpense.expenses || [])
      .filter((expense) => !expense.itinerary)
      .reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  reconcileBillablePurpose() {
    if (!this.travelExpense || !this.travelExpenseStatusDetails) {
      return;
    }

    if (this.isLocked()) {
      return;
    }

    if (!this.hasBillableProjects() && this.travelExpense.purpose === 'customer') {
      this.travelExpense.purpose = '';
      this.travelExpense.project = null;
    }
  }

  onPurposeChange() {
    if (this.travelExpense.purpose === 'customer' && !this.hasBillableProjects()) {
      this.travelExpense.purpose = '';
      this.travelExpense.project = null;
      this.toastr.warning('Billable customer travel requires an assigned customer project.', 'No Projects Available');
      return;
    }

    if (this.travelExpense.purpose !== 'customer') {
      this.travelExpense.project = null;
    }
  }

  addExpense() {
    this.openExpenseModal(null);
  }

  editExpense(expense) {
    if (this.isLocked()) {
      return;
    }
    this.openExpenseModal(expense);
  }

  openExpenseModal(expenseToEdit) {
    const self = this;
    const modalInstance = this.$uibModal.open({
      template: travelExpenseTypeTemplate,
      controller: travelExpenseTypeController,
      controllerAs: "$ctrl",
      resolve: {
        items: function(){
          return [];
        },
        existingExpense: function(){
          return expenseToEdit || null;
        }
      },
      windowClass: "hmodal-success"
    });

    modalInstance.addExpenseToReport = function (expense) {
      self.travelExpense.expenses.push(expense);
    };

    modalInstance.updateExpenseInReport = function(original, updated) {
      self.applyExpenseEdits(original, updated);
    };
  }

  applyExpenseEdits(original, updated) {
    const expenses = this.travelExpense.expenses || [];
    const index = expenses.indexOf(original);

    if (index === -1) {
      return;
    }

    const target = expenses[index];
    target.type = updated.type;
    target.description = updated.description;
    target.amount = updated.amount;
    target.mileage = updated.mileage;
    target.estimateBasisFile = updated.estimateBasisFile;
    target.estimateBasisFileName = updated.estimateBasisFileName;

    if (updated.estimateBasisFile) {
      target.pendingReceiptIdsToDelete = (target.receipts || [])
        .map((receipt) => receipt.receipt || receipt.id)
        .filter(Boolean);
      target.receipts = [];
    }
  }

  addLocation() {
    let self = this;
    let modalInstance = this.$uibModal.open({
      template: locationTemplate,
      controller: locationController,
      controllerAs: "$ctrl",
      resolve: {
        billableTrip() {
          return self.travelExpense.purpose === 'customer';
        },
        existingLocation() {
          return null;
        }
      },
      windowClass: "hmodal-success"
    });

    modalInstance.addLocationToItinerary = function (location) {
      self.travelExpense.expenses.push(location);
    }
  }

  cancel(){
    this.state.go("expenses");
  }

  saveExpenseReport() {
    if (!this.canSave()) {
      this.toastr.warning('Make changes before saving.', 'No Changes');
      return;
    }

    const localReport = this.travelExpense;
    const payload = this.travelExpenseService.serializeExpenseReport(this.travelExpense);
    const hasPendingUploads = (localReport.expenses || []).some((expense) =>
      !expense.itinerary && expense.estimateBasisFile
    );
    const stayOnPage = this.isRejected();
    const savingRejectedChanges = stayOnPage && this.isDirty();

    // A normal save should preserve the current status. In particular, sending REJECTED back
    // as an owner-selected status is invalid; status changes happen through the submit action.
    delete payload.status;
    this.loading = true;
    this.$http.post(
      this.$rootScope.apiHost + '/api/expense-reports/' + this.travelExpense.id,
      payload,
      { withCredentials: true }
    ).then((success) => {
      const finishSave = (savedReport) => {
        this.loading = false;

        if (stayOnPage) {
          const report = this.travelExpenseService.normalizeExpenseReportDates(savedReport || success.data);
          this.travelExpenseService.hydrateEstimateBasisDisplay(report);
          this.travelExpenseService.hydrateMileageDisplay(report);
          this.travelExpense = report;
          this.travelExpense.expenses = report.expenses || [];
          this.captureBaseline();
          this.hasSavedChanges = savingRejectedChanges;
          this.toastr.success(
            savingRejectedChanges
              ? 'Changes saved. You can now resubmit for approval.'
              : 'Expense report saved.',
            'Saved'
          );
          return;
        }

        this.toastr.success(hasPendingUploads ? 'Expense report and documents saved.' : 'Expense report saved.', 'Saved');
        this.state.go('expenses');
      };

      if (!hasPendingUploads) {
        finishSave(success.data);
        return;
      }

      this.travelExpenseService.uploadPendingEstimateBasisFiles(localReport, success.data).then((refreshed) => {
        finishSave(refreshed || success.data);
      }, () => {
        this.loading = false;
        this.toastr.warning('The expense report was saved, but one or more estimate basis documents could not be uploaded.', 'Upload Warning');
        if (stayOnPage) {
          this.captureBaseline();
          this.hasSavedChanges = savingRejectedChanges;
        } else {
          this.state.go('expenses');
        }
      });
    }, (error) => {
      this.loading = false;
      const message = error && error.data && error.data.message
        ? error.data.message
        : 'Could not save the expense report.';
      this.toastr.error(message, 'Save Failed');
    });
  }

  removeExpense(report, index) {
    report.splice(index, 1);
  }

  submitExpenseReport() {
    if (this.isRejected() && !this.canResubmit()) {
      this.toastr.warning('Update the expense report before resubmitting it for approval.', 'No Changes');
      return;
    }

    const submitStatus = () => {
      return this.travelExpenseService.updateExpenseReportStatus(
        this.travelExpense.id,
        this.statusOptions.nextStatus
      ).then(() => {
        this.loading = false;
        this.toastr.success(
          this.isRejected() ? 'Expense report resubmitted for approval.' : 'Expense report submitted.',
          'Submitted'
        );
        this.state.go('expenses');
      }, (error) => {
        this.loading = false;
        const message = error && error.data && error.data.message
          ? error.data.message
          : 'Could not submit the expense report.';
        this.toastr.error(message, 'Submit Failed');
      });
    };

    // When resubmitting a rejected report with unsaved edits, persist them first.
    if (this.isRejected() && this.isDirty()) {
      const localReport = this.travelExpense;
      const payload = this.travelExpenseService.serializeExpenseReport(this.travelExpense);
      delete payload.status;
      this.loading = true;

      this.$http.post(
        this.$rootScope.apiHost + '/api/expense-reports/' + this.travelExpense.id,
        payload,
        { withCredentials: true }
      ).then((success) => {
        const hasPendingUploads = (localReport.expenses || []).some((expense) =>
          !expense.itinerary && expense.estimateBasisFile
        );

        if (!hasPendingUploads) {
          return submitStatus();
        }

        return this.travelExpenseService.uploadPendingEstimateBasisFiles(localReport, success.data)
          .then(() => submitStatus(), () => {
            this.loading = false;
            this.toastr.warning('Changes were saved, but one or more estimate basis documents could not be uploaded. Fix uploads before resubmitting.', 'Upload Warning');
          });
      }, (error) => {
        this.loading = false;
        const message = error && error.data && error.data.message
          ? error.data.message
          : 'Could not save changes before resubmitting.';
        this.toastr.error(message, 'Submit Failed');
      });
      return;
    }

    this.loading = true;
    submitStatus();
  }

  isLocked() {
    if (this.travelExpenseService.isProxyImportEditable(this.travelExpense)) {
      return false;
    }
    return !this.travelExpenseStatusDetails.editable
  }


}

export default TravelExpenseDetailsController;



// WEBPACK FOOTER //
// ./client/pages/expenses/travel-expense-details/travel-expense-details.controller.js